import React, { useState, useRef, useEffect } from 'react';

import { type Project } from 'Common/hooks/useFilters';

import Section from 'Elements/Section';

import Grid, { Col } from 'Components/Grid';
import Accordion from 'Components/Accordion';

import SearchListing, { type SearchListingProps } from 'Modules/Listing/Search';

import {
    TabsWrapper,
    TabList,
    TabButton,
    FiltersDesktop,
    FiltersMobile,
    FilterHeading,
    CheckboxList
} from './Search.styled';

interface Tab {
    label: string;
    activeTabId: Project;
}

export interface Props {
    tabs: Tab[];
    activeProject?: Project;
    contentTypesFilters: React.ReactNode;
    listing: SearchListingProps;
    handleProjectSelect: (project: Project) => void;
}

const Search: React.FC<Props> = props => {
    const { tabs, activeProject, contentTypesFilters, listing, handleProjectSelect } = props;

    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const tabsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const tabIndex = tabs.findIndex(tab => tab.activeTabId === activeProject);

        if (tabIndex !== -1) {
            setActiveTabIndex(tabIndex);
        }
    }, [activeProject, tabs]);

    const handleTabClick = (index: number) => {
        const selectedTab = tabs[index];
        setActiveTabIndex(index);

        handleProjectSelect(selectedTab.activeTabId);
    };

    const handleTabKeydown = (event: React.KeyboardEvent) => {
        if (tabsRef.current && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            const tabsEl = tabsRef.current;
            let newIndex: number;

            if (event.key === 'ArrowLeft') {
                newIndex = activeTabIndex - 1 < 0 ? tabs.length - 1 : activeTabIndex - 1;
            } else {
                newIndex = activeTabIndex + 1 > tabs.length - 1 ? 0 : activeTabIndex + 1;
            }
            (tabsEl.children[newIndex] as HTMLButtonElement).focus();

            handleTabClick(newIndex);
        }
    };

    return (
        <Section id="search-results" paddingTop={0.5}>
            <Grid>
                <Col l={9}>
                    <TabsWrapper>
                        <TabList role="tablist" aria-label="Tab list" ref={tabsRef}>
                            {tabs.map((tab, index) => (
                                <TabButton
                                    id={`tab-${tab.activeTabId}`}
                                    key={`tab-${tab.activeTabId}`}
                                    role="tab"
                                    aria-selected={index === activeTabIndex}
                                    aria-controls={`panel-${tabs[activeTabIndex].activeTabId}`}
                                    tabIndex={index === activeTabIndex ? 0 : -1}
                                    onClick={() => handleTabClick(index)}
                                    onKeyDown={handleTabKeydown}
                                    $isActive={index === activeTabIndex}
                                    $project={tab.activeTabId}
                                >
                                    {tab.label}
                                </TabButton>
                            ))}
                        </TabList>
                    </TabsWrapper>
                    <FiltersMobile>
                        <Accordion
                            id="mobile-filters"
                            button={{ ariaLabel: 'Open filters' }}
                            heading={<FilterHeading>Open filters</FilterHeading>}
                            variant="white"
                        >
                            <CheckboxList>{contentTypesFilters}</CheckboxList>
                        </Accordion>
                    </FiltersMobile>
                    <div
                        id={`panel-${tabs[activeTabIndex].activeTabId}`}
                        role="tabpanel"
                        aria-labelledby={`tab-${tabs[activeTabIndex].activeTabId}`}
                    >
                        <SearchListing {...listing} />
                    </div>
                </Col>
                <Col l={3}>
                    <FiltersDesktop>
                        <FilterHeading>Filter results by:</FilterHeading>
                        <CheckboxList>{contentTypesFilters}</CheckboxList>
                    </FiltersDesktop>
                </Col>
            </Grid>
        </Section>
    );
};

export default Search;
